/**
 * Handles price input changes by cleaning the value and validating format
 * @param value - The input value to process
 * @param onChange - Callback function to update the value
 */
export function handlePriceChange(value: string, onChange: (value: string) => void): void {
	// Remove $ and commas, keep only numbers and decimal
	const cleanValue = value.replace(/[$,]/g, '');
	// Only allow numbers, one decimal point, and empty string
	if (cleanValue === '' || /^\d*\.?\d*$/.test(cleanValue)) {
		onChange(cleanValue);
	}
}

/**
 * Formats a string as a currency value
 * @param value - The string to format
 * @returns The formatted currency value
 */
export function formatDisplayValue(value: string | undefined): string | undefined {
	return value && !isNaN(Number(value)) ? `$${Number(value).toLocaleString()}` : value;
}

/**
 * Formats a number with commas
 * @param value - The number to format
 * @returns The formatted number with commas
 */
export const formatNumberWithCommas = (value: number | string): string => {
	// Convert to string and handle empty/invalid input
	const numStr = String(value);
	if (!numStr || numStr === 'NaN') return '0';

	// Split into integer and decimal parts
	const [integer, decimal] = numStr.split('.');

	// Add commas to integer part
	const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	// Return with decimal part if it exists
	return decimal !== undefined ? `${formattedInteger}.${decimal}` : formattedInteger;
};

/**
 * Formats a number as currency with $ sign
 * Returns formatted string or placeholder if value is null/undefined
 * @param showCents - When false, rounds to whole dollars (e.g. for dashboard stats)
 */
export function formatCurrency(
	value: number | null | undefined,
	placeholder = '—',
	showCents = true,
): string {
	if (value == null) {
		return placeholder;
	}
	return `$${value.toLocaleString('en-US', {
		minimumFractionDigits: showCents ? 2 : 0,
		maximumFractionDigits: showCents ? 2 : 0,
	})}`;
}

/**
 * Formats a number as a string
 * Returns formatted string or placeholder if value is undefined
 * Used for displaying counts and stats
 */
export function formatNumber(value: number | undefined, placeholder = '—'): string {
	if (value === undefined) {
		return placeholder;
	}
	return value.toString();
}

/**
 * Formats a number as a percentage (e.g., 0.85 → "85%")
 * Returns formatted string or placeholder if value is undefined
 * Used for displaying rates and ratios
 */
export function formatPercentage(value: number | undefined, placeholder = '—'): string {
	if (value === undefined) {
		return placeholder;
	}
	return `${Math.round(value * 100)}%`;
}

/**
 * Formats a number as days (e.g., "5 days")
 * Returns formatted string or placeholder if value is undefined
 * Used for displaying time periods
 */
export function formatDays(value: number | undefined, placeholder = '—'): string {
	if (value === undefined) {
		return placeholder;
	}
	return `${value} days`;
}
